import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BALLS = [
  { name: "red", value: 1, color: "#D62828", text: "white" },
  { name: "yellow", value: 2, color: "#F6BD60", text: "#1a1a1a" },
  { name: "green", value: 3, color: "#2D6A4F", text: "white" },
  { name: "brown", value: 4, color: "#8B5E3C", text: "white" },
  { name: "blue", value: 5, color: "#1D3557", text: "white" },
  { name: "pink", value: 6, color: "#E76F8B", text: "white" },
  { name: "black", value: 7, color: "#1a1a1a", text: "white", border: "1px solid #444" },
];

const COLOR_ORDER = ["yellow", "green", "brown", "blue", "pink", "black"];

export default function SnookerScorer({ matchId, match, onScoreUpdate }) {
  const [loading, setLoading] = useState(false);
  const [foulValue, setFoulValue] = useState("4");
  const [redCount, setRedCount] = useState(1);

  const currentFrame = match?.frames?.find(f => !f.is_complete);
  const currentTurn = currentFrame?.current_turn || 1;
  const redsRemaining = currentFrame?.reds_remaining ?? 15;
  const colorsPotted = currentFrame?.colors_potted || [];
  const expectColor = currentFrame?.expect_color || false;
  const currentBreak = currentFrame?.[`current_break_${currentTurn}`] || 0;
  const inEndgame = redsRemaining <= 0 && !expectColor;

  const activePlayerName = currentTurn === 1 ? match?.player1_name : match?.player2_name;
  const p1Points = currentFrame?.player1_points || 0;
  const p2Points = currentFrame?.player2_points || 0;

  // Keep the multi-red stepper within valid bounds as reds are potted.
  useEffect(() => {
    setRedCount(c => {
      const max = Math.max(1, redsRemaining);
      if (c > max) return max;
      if (c < 1) return 1;
      return c;
    });
  }, [redsRemaining]);

  // Determine which balls are enabled
  const isBallEnabled = (ballName) => {
    if (ballName === "red") {
      // Red disabled if no reds remaining or if we expect a color after potting a red
      return redsRemaining > 0 && !expectColor;
    }
    // During normal play (reds still on table): colors always available after a red
    if (redsRemaining > 0) return true;
    // Endgame: colors must be potted in order, disable already-potted ones
    if (inEndgame) {
      if (colorsPotted.includes(ballName)) return false;
      // Only allow next color in order
      const nextColor = COLOR_ORDER.find(c => !colorsPotted.includes(c));
      return ballName === nextColor;
    }
    // After last red, one final color before endgame
    if (expectColor) return ballName !== "red";
    return true;
  };

  const doAction = useCallback(async (action, ball = null, extraData = {}) => {
    if (loading) return;
    setLoading(true);
    try {
      const payload = { action, ...extraData };
      if (ball) payload.ball = ball;
      await axios.post(`${API}/matches/${matchId}/snooker-pot`, payload, { withCredentials: true });
      // Reset the multi-red stepper back to 1 after every action
      setRedCount(1);
      onScoreUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scoring failed");
    } finally {
      setLoading(false);
    }
  }, [matchId, loading, onScoreUpdate]);

  const handleFrameComplete = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      // Auto-winner: player with highest points
      const res = await axios.post(`${API}/matches/${matchId}/frame-complete`, {}, { withCredentials: true });
      if (res.data.match_complete) {
        toast.success("Match complete!");
      } else {
        toast.success(`Frame complete! ${res.data.player1_frames}-${res.data.player2_frames}`);
      }
      onScoreUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  }, [matchId, loading, onScoreUpdate]);

  if (!currentFrame) {
    return (
      <Card className="bg-card border-secondary/30">
        <CardContent className="p-6 text-center text-muted-foreground">
          No active frame. Match may be complete.
        </CardContent>
      </Card>
    );
  }

  // Determine frame leader for auto-winner display
  const leader = p1Points > p2Points ? match?.player1_name : p2Points > p1Points ? match?.player2_name : "Tied";

  return (
    <Card className="bg-card border-secondary/30" data-testid="snooker-scorer">
      <CardContent className="p-5 space-y-5">
        {/* Frame Score Header */}
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">
            Frame {currentFrame.frame_number} — Referee Scoring
          </h3>
          <div className="flex items-center gap-2">
            {inEndgame && (
              <Badge className="bg-chart-3/20 text-chart-3 border-chart-3/30" data-testid="endgame-badge">Colors</Badge>
            )}
            <Badge className="bg-secondary/20 text-secondary border-secondary/30">
              {redsRemaining > 0 ? `${redsRemaining} reds` : "Reds done"}
            </Badge>
          </div>
        </div>

        {/* Frame Score */}
        <div className="grid grid-cols-3 items-center text-center">
          <div className={`p-3 rounded-lg transition-all ${currentTurn === 1 ? "bg-primary/15 ring-2 ring-primary/40" : "bg-muted/20"}`}>
            <p className="text-xs text-muted-foreground mb-1">{match?.player1_name?.split(' ')[0]}</p>
            <p className="font-heading text-3xl font-bold" data-testid="p1-frame-score">{p1Points}</p>
          </div>
          <div className="text-muted-foreground text-lg">vs</div>
          <div className={`p-3 rounded-lg transition-all ${currentTurn === 2 ? "bg-primary/15 ring-2 ring-primary/40" : "bg-muted/20"}`}>
            <p className="text-xs text-muted-foreground mb-1">{match?.player2_name?.split(' ')[0]}</p>
            <p className="font-heading text-3xl font-bold" data-testid="p2-frame-score">{p2Points}</p>
          </div>
        </div>

        {/* Active Player Indicator */}
        <div className="text-center py-2 rounded-lg bg-primary/10 border border-primary/20">
          <p className="text-sm">
            <span className="text-muted-foreground">At the table: </span>
            <span className="font-semibold text-primary" data-testid="active-player">{activePlayerName}</span>
          </p>
          {currentBreak > 0 && (
            <p className="text-xs text-secondary mt-1">Current break: <span className="font-bold">{currentBreak}</span></p>
          )}
        </div>

        {/* Ball Buttons */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Pot a Ball</p>
            {/* Multi-red stepper — snooker permits potting 2+ reds on a single stroke */}
            <div
              className={`flex items-center gap-2 rounded-full border border-border bg-muted/20 pl-3 pr-1 py-1 transition-opacity ${
                redsRemaining > 1 && !expectColor ? "opacity-100" : "opacity-40 pointer-events-none"
              }`}
              data-testid="multi-red-stepper"
              title={redsRemaining > 1 ? "Reds potted on this shot" : "Multi-red not available"}
            >
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Reds / shot</span>
              <button
                type="button"
                onClick={() => setRedCount(c => Math.max(1, c - 1))}
                disabled={loading || redCount <= 1}
                className="w-6 h-6 rounded-full bg-background border border-border text-sm leading-none disabled:opacity-30 hover:bg-primary/10"
                data-testid="multi-red-decrement"
              >−</button>
              <span className="w-5 text-center font-bold text-sm tabular-nums" data-testid="multi-red-count">{redCount}</span>
              <button
                type="button"
                onClick={() => setRedCount(c => Math.min(redsRemaining, c + 1))}
                disabled={loading || redCount >= redsRemaining}
                className="w-6 h-6 rounded-full bg-background border border-border text-sm leading-none disabled:opacity-30 hover:bg-primary/10"
                data-testid="multi-red-increment"
              >+</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            {BALLS.map(ball => {
              const enabled = isBallEnabled(ball.name);
              const isRed = ball.name === "red";
              const showCountBadge = isRed && enabled && redCount > 1;
              return (
                <button
                  key={ball.name}
                  onClick={() => enabled && doAction("pot", ball.name, isRed ? { count: redCount } : {})}
                  disabled={loading || !enabled}
                  className={`group relative flex flex-col items-center gap-1.5 transition-all ${
                    enabled ? "hover:scale-110 active:scale-95" : "opacity-25 cursor-not-allowed"
                  }`}
                  data-testid={`pot-${ball.name}`}
                  title={enabled ? (isRed && redCount > 1 ? `Pot ${redCount} reds (+${redCount})` : `${ball.name} (${ball.value})`) : `${ball.name} - disabled`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm shadow-lg transition-shadow ${enabled ? "group-hover:shadow-xl" : ""}`}
                    style={{ backgroundColor: ball.color, color: ball.text, border: ball.border || "none" }}
                  >
                    {ball.value}
                  </div>
                  <span className="text-[10px] capitalize text-muted-foreground">{ball.name}</span>
                  {showCountBadge && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold shadow"
                      data-testid="pot-red-count-badge"
                    >×{redCount}</span>
                  )}
                  {!enabled && colorsPotted.includes(ball.name) && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-muted flex items-center justify-center text-[8px]">x</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => doAction("foul", null, { foul_points: parseInt(foulValue) })}
              disabled={loading}
              className="border-destructive/40 text-destructive hover:bg-destructive/10 h-12 flex-1"
              data-testid="foul-btn"
            >
              <span className="w-5 h-5 rounded-full bg-destructive/20 flex items-center justify-center mr-2 text-xs font-bold">!</span>
              Foul
            </Button>
            <Select value={foulValue} onValueChange={setFoulValue}>
              <SelectTrigger className="w-16 h-12 bg-background border-destructive/30 text-sm" data-testid="foul-value-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4</SelectItem>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="6">6</SelectItem>
                <SelectItem value="7">7</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={() => doAction("switch_turn")}
            disabled={loading}
            className="border-border h-12"
            data-testid="switch-turn-btn"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
            Miss / Switch
          </Button>
        </div>

        {/* Highlight Tagging */}
        <div className="border-t border-border pt-4">
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Tag Highlight</p>
          <div className="flex flex-wrap gap-2">
            {[
              { type: "big_break", label: "Big Break", desc: `Break ${currentBreak || 0}` },
              { type: "turning_point", label: "Turning Point" },
              { type: "clutch", label: "Clutch Moment" },
              { type: "notable", label: "Notable Play" },
            ].map(h => (
              <Button
                key={h.type}
                variant="outline"
                size="sm"
                disabled={loading}
                className="text-xs border-secondary/30 text-secondary hover:bg-secondary/10"
                data-testid={`highlight-${h.type}-btn`}
                onClick={async () => {
                  try {
                    const activePlayerId = currentTurn === 1 ? match?.player1_id : match?.player2_id;
                    const activePlayerName = currentTurn === 1 ? match?.player1_name : match?.player2_name;
                    await axios.post(`${API}/matches/${matchId}/highlight`, {
                      type: h.type,
                      description: `${h.label}${h.type === "big_break" ? ` (${currentBreak})` : ""} by ${activePlayerName}`,
                      frame_number: currentFrame.frame_number,
                      player_id: activePlayerId,
                      break_value: h.type === "big_break" ? currentBreak : null,
                    }, { withCredentials: true });
                    toast.success(`Highlight tagged: ${h.label}`);
                  } catch (err) {
                    toast.error("Failed to tag highlight");
                  }
                }}
              >
                {h.label}
              </Button>
            ))}
          </div>
        </div>

        {/* End Frame - Auto winner by highest points */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">End Frame</p>
            <p className="text-xs text-muted-foreground">
              Leading: <span className="text-primary font-medium">{leader}</span>
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleFrameComplete}
            disabled={loading}
            className="w-full border-primary/30 text-primary hover:bg-primary/10 h-11"
            data-testid="frame-complete-btn"
          >
            End Frame — {leader} {leader !== "Tied" ? "Wins" : "(Tied)"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
